import * as React from 'react';
import type { SelectOption } from '@ooh/ui/components/select';
import {
  availabilityImportEndpoints,
  suppliers,
  type AvailabilityImportPreviewResponse,
} from '@ooh/contracts';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { ErrorBoundary } from '../../layouts/error-boundary';
import { QueryBoundary } from '../../layouts/query-boundary';
import { TableSkeleton } from '../../layouts/skeletons';
import { previewRequest } from '../api/queries';
import { useResolveFreshAvailabilityProfile } from '../api/mutations';
import { resolveCarryOverSupplierName } from '../lib/preview-confirm-gating';
import { PreviewPhaseEditor } from './preview-phase-editor';

interface PreviewPhaseProps {
  jobId: number;
  /** The operator's manual header-row override from the header-row wizard
   * step (CU-86cb1npz2, gap #1), `undefined` for the common case where the
   * auto-detected row was kept. Threaded into the preview request so this
   * component's own fetch lands in the same cache slot the header-row
   * phase's prefetch already populated. */
  headerRowIndex?: number;
  onBack: () => void;
  onConfirmed: () => void;
}

/**
 * Preview phase: fetches the resolved profile once via
 * `GET /availability-import/:id/preview`, then hands off to the editable
 * editor. The GET endpoint's request schema is `{ id }` only — it cannot
 * recompute from an edited profile (see findings.md's period_label_map
 * gotcha) — so this query is fetched once and the editor recomputes
 * displayed rows client-side on every edit (`deriveInterpretedRows`).
 */
export function PreviewPhase(props: PreviewPhaseProps) {
  return (
    <QueryBoundary fallback={<TableSkeleton />}>
      <PreviewPhaseLoader {...props} />
    </QueryBoundary>
  );
}

function PreviewPhaseLoader({ jobId, headerRowIndex, onBack, onConfirmed }: PreviewPhaseProps) {
  // Seeded once into the editor's local state, so a refetch must not swap the
  // rows or profile under it (hence `staleTime: Infinity`).
  const { data: previewData } = useApiSuspenseQuery(
    availabilityImportEndpoints.preview,
    previewRequest(jobId, headerRowIndex),
    { staleTime: Infinity },
  );

  // `effectiveData`/`resetGeneration` back the "Wyczyść mapowanie" reset
  // action (CU-86cbafj4j): a remembered mapping predating a supported field
  // can leave every row unmatched with no way to recover short of
  // re-uploading. On a successful `resolve-fresh` call, the RAW fresh
  // response (unmerged — matching what the server actually computed the
  // rows against) replaces `previewData` as the effective preview data AND
  // the generation counter folds into `PreviewPhaseEditor`'s `key`, forcing
  // a full remount so `usePreviewProfileEditor`'s local state and
  // `useRematchedRows`' debounce state both reseed from the fresh profile
  // rather than merging with stale local edits. Any previously-known
  // `defaultSupplierName` to carry over is tracked separately
  // (`carryOverSupplierName`) and applied post-mount — see
  // `resolveCarryOverSupplierName`'s docstring for why.
  const [effectiveData, setEffectiveData] =
    React.useState<AvailabilityImportPreviewResponse | null>(null);
  const [resetGeneration, setResetGeneration] = React.useState(0);
  const [carryOverSupplierName, setCarryOverSupplierName] = React.useState<string | undefined>(
    undefined,
  );
  const resolveFreshMutation = useResolveFreshAvailabilityProfile(jobId, (response) => {
    const previousProfile = (effectiveData ?? previewData)?.profile;
    setCarryOverSupplierName(resolveCarryOverSupplierName(response, previousProfile));
    setEffectiveData(response);
    setResetGeneration((g) => g + 1);
  });
  // Forwards the currently-effective profile's headerRowIndex so a
  // "Wyczyść mapowanie" reset re-parses against the operator's already-
  // confirmed header row (CU-86cb1npz2, gap #1) instead of silently
  // reverting to detectHeaderRow's auto-detected one.
  const currentHeaderRowIndex = (effectiveData ?? previewData)?.profile.headerRowIndex;

  return (
    <SupplierOptions>
      {(realSupplierOptions) => (
        <PreviewPhaseEditor
          key={`${jobId}-${resetGeneration}`}
          jobId={jobId}
          initialData={effectiveData ?? previewData}
          realSupplierOptions={realSupplierOptions}
          carryOverSupplierName={carryOverSupplierName}
          onReset={() => resolveFreshMutation.mutate(currentHeaderRowIndex)}
          resetting={resolveFreshMutation.isPending}
          onBack={onBack}
          onConfirmed={onConfirmed}
        />
      )}
    </SupplierOptions>
  );
}

interface SupplierOptionsProps {
  children: (options: SelectOption[]) => React.ReactNode;
}

function LoadedSupplierOptions({ children }: SupplierOptionsProps) {
  const { data } = useApiSuspenseQuery(suppliers.list, {});
  return children(data.map((supplier) => ({ value: supplier.name, label: supplier.name })));
}

/** Real platform suppliers for the "Dostawca" selector's defaultSupplierName fallback. A failed
 * fetch degrades to an empty option list rather than blocking the preview. */
function SupplierOptions({ children }: SupplierOptionsProps) {
  return (
    <ErrorBoundary onReset={() => undefined} renderError={() => children([])}>
      <React.Suspense fallback={<TableSkeleton />}>
        <LoadedSupplierOptions>{children}</LoadedSupplierOptions>
      </React.Suspense>
    </ErrorBoundary>
  );
}
